/**
 * SEASONS, AND THE CARD YEAR THEY BELONG TO.
 *
 * The NMJL issues a new card every spring, and hand values change with it. Season rankings are
 * built from raw point totals, so **the card is a unit of measurement** — comparing a score played
 * on the 2027 card against one played on the 2028 card is comparing two different things. Aligning
 * seasons to the card year is what keeps every season played entirely on one card.
 *
 * ⚠ WINTER IS THE TRAP, AND IT IS THE WHOLE REASON THIS MODULE EXISTS.
 *
 * Winter is played in **January to March of the FOLLOWING calendar year** but belongs to the
 * **PREVIOUS card year**. A session on 12 January 2028 is Winter of card year 2027. Get it wrong
 * and a player's winter results are filed under a card she was not playing, which is invisible
 * until somebody ranks a season and the numbers are quietly incomparable.
 *
 *   Spring   Apr-Jun   calendar year == card year
 *   Summer   Jul-Sep   calendar year == card year
 *   Fall     Oct-Dec   calendar year == card year
 *   Winter   Jan-Mar   calendar year == card year + 1     <-- the trap
 *
 * It is displayed as "Winter 2027-28", never "Winter 2028", because the second is ambiguous to the
 * one audience that cares: a player deciding whether two of her scores belong in the same ranking.
 *
 * PURE, like `../time` next door — dates in, season out, no clock of its own and no default. The
 * time module's own header says a default is what got the platform into trouble with zones, and
 * the same reasoning applies here: nothing in this file guesses what "now" is.
 *
 * A SESSION BELONGS TO THE SEASON ITS DATE FALLS IN. Not the league's season, not the season it was
 * scheduled in — the date. A league that runs late into January has its last session in Winter, and
 * that is correct rather than an edge case to smooth over.
 */

export const SEASON_NAMES = ['spring', 'summer', 'fall', 'winter'] as const;

export type SeasonName = (typeof SEASON_NAMES)[number];

export interface Season {
  name: SeasonName;
  /**
   * The NMJL card the season is played on. For winter this is one LESS than the calendar year the
   * session falls in — see the header.
   */
  cardYear: number;
}

/**
 * Which season a date falls in, and which card year that season belongs to.
 *
 * The month is read in the LEAGUE's zone by the caller, not here: a session at 7pm on 31 March in
 * Denver is 1 April in UTC, which would file the last session of Spring under Summer. Callers pass
 * the local calendar month and year, which `../time` can produce for a zone.
 */
export function seasonFor(localYear: number, localMonth: number): Season {
  if (localMonth < 1 || localMonth > 12) {
    throw new Error(`Month must be 1-12, got ${localMonth}.`);
  }

  if (localMonth >= 4 && localMonth <= 6) return { name: 'spring', cardYear: localYear };
  if (localMonth >= 7 && localMonth <= 9) return { name: 'summer', cardYear: localYear };
  if (localMonth >= 10) return { name: 'fall', cardYear: localYear };

  /* January to March: played this calendar year, belongs to LAST year's card. */
  return { name: 'winter', cardYear: localYear - 1 };
}

/**
 * How a season is written down for a person.
 *
 * Winter spans two calendar years and says so. The others do not, and adding a range to them would
 * be noise — "Spring 2027-27" is worse than "Spring 2027".
 */
export function seasonLabel(season: Season): string {
  const capitalised = season.name[0]!.toUpperCase() + season.name.slice(1);

  if (season.name !== 'winter') return `${capitalised} ${season.cardYear}`;

  /* "Winter 2027-28". Two digits on the second year, because "2027-2028" reads as a range of
     seasons rather than one season that crosses new year. */
  const secondYear = String(season.cardYear + 1).slice(-2);
  return `${capitalised} ${season.cardYear}-${secondYear}`;
}

/** The calendar months a season is played in, in the order they occur. */
export function monthsOf(name: SeasonName): readonly number[] {
  switch (name) {
    case 'spring':
      return [4, 5, 6];
    case 'summer':
      return [7, 8, 9];
    case 'fall':
      return [10, 11, 12];
    case 'winter':
      return [1, 2, 3];
  }
}

/**
 * The first and last calendar dates of a season, as year-month-day parts.
 *
 * Returned as parts rather than `Date` on purpose: a season's boundary is a LOCAL calendar date in
 * a market's zone, and turning it into an instant here would bake in whichever zone this module
 * happened to assume. The caller has the zone; it does not.
 */
export function seasonBounds(season: Season): {
  startsOn: { year: number; month: number; date: number };
  endsOn: { year: number; month: number; date: number };
} {
  const months = monthsOf(season.name);
  const first = months[0]!;
  const last = months[months.length - 1]!;

  /* Winter is played the year AFTER its card year. Everything else is played in it. */
  const calendarYear = season.name === 'winter' ? season.cardYear + 1 : season.cardYear;

  /* Day 0 of the next month is the last day of this one, which sidesteps leap years entirely. */
  const lastDate = new Date(Date.UTC(calendarYear, last, 0)).getUTCDate();

  return {
    startsOn: { year: calendarYear, month: first, date: 1 },
    endsOn: { year: calendarYear, month: last, date: lastDate },
  };
}

/** Whether two seasons are the same one. Rankings are per season, so this decides what compares. */
export function sameSeason(a: Season, b: Season): boolean {
  return a.name === b.name && a.cardYear === b.cardYear;
}

/**
 * WHICH CARDS A LEAGUE STARTING ON THIS DATE MAY BE PLAYED ON.
 *
 * One card, except in the changeover. The NMJL releases its card after Lunar New Year; it reaches
 * most people in mid-March and some not until mid-April, so a league starting in either of those
 * months could honestly be on the old card or the new one, and the director is the only person who
 * knows which her players will be holding.
 *
 *   January, February   last year's card. The new one does not exist yet.
 *   March, April        either. Her choice, and the only months where there is one.
 *   May onwards         the current card. Everybody has it.
 *
 * Never a free number. A card year that is neither of these is a typo, and the damage is silent —
 * a season's scores filed against a card nobody played on still renders correctly on every screen.
 */
export function allowedCardYears(localYear: number, localMonth: number): readonly number[] {
  const derived = seasonFor(localYear, localMonth).cardYear;

  /* The two cards in play around the release are last year's and this year's, whichever month. */
  if (localMonth === 3 || localMonth === 4) return [localYear - 1, localYear];

  return [derived];
}
